"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@repo/shared";
import type { UserRole } from "@repo/shared";
import { safeFetch } from "@/lib/safe-fetch";

const AUTH_KEY = "efakt-auth";
const AUTH_BACKUP_KEY = "efakt-auth-backup";

/**
 * Try to read valid auth state from localStorage (primary) or
 * sessionStorage (backup — survives extension interference).
 */
function readAuthFromStorage(): { user: { username: string; role: UserRole; userId: number }; token: string } | null {
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      const key = storage === window.localStorage ? AUTH_KEY : AUTH_BACKUP_KEY;
      const raw = storage.getItem(key);
      if (!raw) continue;
      const { state } = JSON.parse(raw);
      if (state?.isAuthenticated && state?.token && state?.user) {
        // Ensure userId is populated (may be missing from older sessions)
        if (!state.user.userId && state.token) {
          try {
            const payload = JSON.parse(atob(state.token.split(".")[1]));
            state.user.userId = parseInt(payload.userId ?? "0", 10);
          } catch { /* ignore */ }
        }
        return { user: state.user, token: state.token };
      }
    } catch {
      // skip this storage
    }
  }
  return null;
}

/**
 * Save auth state to sessionStorage as a backup.
 * This protects against Chrome extensions that clear localStorage.
 */
function backupAuthToSession(user: object, token: string) {
  try {
    window.sessionStorage.setItem(
      AUTH_BACKUP_KEY,
      JSON.stringify({ state: { user, token, isAuthenticated: true }, version: 0 }),
    );
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

/**
 * AuthGuard — protects routes behind authentication.
 *
 * On mount it reads the auth session from localStorage (with sessionStorage
 * as backup), populates the Zustand store for child components, checks
 * backend setup status, and only then renders children.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // 1. Read auth from storage (localStorage → sessionStorage fallback)
    const auth = readAuthFromStorage();

    if (!auth) {
      router.push("/login");
      return;
    }

    // 2. Populate Zustand store for child components + backup to sessionStorage
    useAuthStore.setState({
      user: auth.user,
      token: auth.token,
      isAuthenticated: true,
    });
    backupAuthToSession(auth.user, auth.token);

    // 3. Check backend setup status
    let cancelled = false;

    (async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        const res = await safeFetch("/api/backend/setup/status", {
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (res.ok) {
          const data = await res.json();
          if (data.setupRequired) {
            router.push("/setup");
            return;
          }
        }
      } catch {
        // Can't reach backend — proceed anyway
      }
      if (!cancelled) setReady(true);
    })();

    return () => { cancelled = true; };
  }, [router]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
